import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useApi } from '../hooks/useApi';
import { useDebounce } from '../hooks/useDebounce';
import logger from '../utils/logger';
import env from '../config/env';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Select from 'react-select';
import { Search, PackageSearch, Loader2 } from 'lucide-react';

function KerkoStokShpejt() {
  const { roli, lokacion_id } = useAuth();
  const { theme } = useTheme();
  const [query, setQuery] = useState('');
  const [lokacionSelect, setLokacionSelect] = useState(null);
  const debouncedQuery = useDebounce(query, 400);

  const { data: lokacionetData } = useApi(
    () => apiService.get('/lokacionet'),
    [],
    { immediate: true, showNotifications: false }
  );

  const lokacionet = Array.isArray(lokacionetData) ? lokacionetData : (lokacionetData?.data || []);

  const { data: rezultateData, loading: isLoading } = useApi(
    () => {
      if (!debouncedQuery.trim()) return Promise.resolve([]);
      const params = { q: debouncedQuery };
      if (lokacionSelect) params.lokacion_id = lokacionSelect;
      return apiService.get('/kerkimi-stokut-shpejt', { params });
    },
    [debouncedQuery, lokacionSelect],
    { immediate: !!debouncedQuery.trim(), showNotifications: false }
  );

  const rezultate = Array.isArray(rezultateData) 
    ? rezultateData.filter(p => p.eshte_aktiv !== 2)
    : ((rezultateData?.data || []).filter(p => p.eshte_aktiv !== 2));

  useEffect(() => {
    if (roli !== 'admin' && lokacion_id) {
      setLokacionSelect(parseInt(lokacion_id));
    }
  }, [roli, lokacion_id]);

  // Custom styles for react-select based on theme
  const selectStyles = theme === 'dark' ? {
    control: (base, state) => ({
      ...base,
      backgroundColor: '#1e293b',
      borderColor: state.isFocused ? '#f59e0b' : '#475569',
      boxShadow: state.isFocused ? '0 0 0 1px #f59e0b' : 'none',
      '&:hover': { borderColor: '#f59e0b' },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: '#1e293b',
      borderColor: '#475569',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#78350f' : state.isSelected ? '#f59e0b' : '#1e293b',
      color: state.isSelected ? 'white' : '#e2e8f0',
    }),
    singleValue: (base) => ({ ...base, color: '#e2e8f0' }),
    input: (base) => ({ ...base, color: '#e2e8f0' }),
    placeholder: (base) => ({ ...base, color: '#94a3b8' }),
  } : {
    control: (base, state) => ({
      ...base,
      backgroundColor: 'transparent',
      borderColor: state.isFocused ? '#f59e0b' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 1px #f59e0b' : 'none',
      '&:hover': { borderColor: '#f59e0b' },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: 'white',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#fef3c7' : state.isSelected ? '#fbbf24' : 'white',
      color: state.isSelected ? 'white' : '#111827',
    }),
  };

  return (
    <div className="w-full animate-fade-in">
      <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gradient-logo flex items-center gap-3 animate-slide-down">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
          <PackageSearch size={32} className="text-primary-600 dark:text-primary-400" />
        </div>
        Kërko Stokun
      </h2>

      <div className="card-premium p-6 mb-10 flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="relative w-full md:flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={18} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Kërko sipas emrit ose kodit të produktit..."
            className="input-premium pl-10 pr-12"
          />
          {isLoading && <Loader2 className="animate-spin absolute right-4 top-1/2 -translate-y-1/2 text-primary-500 dark:text-primary-400" size={20} />}
        </div>

        <div className="w-full md:w-64">
          <Select
            options={ roli === 'admin'
              ? [{ value: '', label: 'Të gjitha lokacionet' }, ...lokacionet.map(l => ({ value: l.id, label: l.emri }))]
              : [{ value: lokacion_id, label: lokacionet.find(l => l.id === parseInt(lokacion_id))?.emri }, 
                 ...lokacionet.filter(l => l.id !== parseInt(lokacion_id)).map(l => ({ value: l.id, label: l.emri }))]}
            value={
              lokacionSelect === ''
                ? { value: '', label: 'Të gjitha lokacionet' }
                : lokacionet
                    .map(l => ({ value: l.id, label: l.emri }))
                    .find(l => l.value === Number(lokacionSelect)) || null
            }
            onChange={(option) => setLokacionSelect(option?.value || '')}
            placeholder="Zgjidh lokacionin"
            isClearable
            styles={selectStyles}
            classNamePrefix="react-select"
          />
        </div>
      </div>

      {rezultate.length > 0 ? (
        <div className="space-y-8">
          {rezultate.map((produkt) => (
            <div key={produkt.id} className="card-premium hover:shadow-premium-lg dark:hover:shadow-premium-lg-dark transition-all duration-300">
              <div className="flex flex-col md:flex-row gap-6 p-6 items-start">
                {produkt.foto?.length > 0 && (
                  <img
                    src={`${env.API_URL_Image}/storage/${produkt.foto[0].file_path}`}
                    alt={produkt.emri}
                    className="w-36 h-36 object-cover rounded-xl shadow-md"
                  />
                )}

                <div className="flex-1">
                  <h3 className={`text-xl font-semibold mb-2 ${produkt.stok?.some(s => s.detaje?.some(d => d.sasia > 0)) ? 'text-primary-700 dark:text-primary-400' : 'text-red-600 dark:text-red-400'}`}>
                    {produkt.emri} <span className="text-sm text-gray-500 dark:text-gray-400">({produkt.kodi})</span>
                  </h3>

                  {Array.isArray(produkt.stok) && produkt.stok[0]?.lokacion ? (
                    produkt.stok.map((grup, idx) => (
                      <div key={idx} className="mb-6">
                        <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-2">{grup.lokacion.emri}</h4>
                        <div className="card-premium overflow-x-auto p-0">
                          <table className="table-premium">
                            <thead>
                              <tr>
                                <th>Madhësia</th>
                                <th>Ngjyra</th>
                                <th>Sasia në stok</th>
                              </tr>
                            </thead>
                            <tbody>
                              {grup.detaje.map((s, i) => (
                                <tr key={i}>
                                  <td>{s.madhesi?.emri}</td>
                                  <td>{s.ngjyra?.emri}</td>
                                  <td className={`font-semibold ${s.sasia > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{s.sasia}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="card-premium overflow-x-auto p-0">
                      <table className="table-premium">
                        <thead>
                          <tr>
                            <th>Madhësia</th>
                            <th>Ngjyra</th>
                            <th>Sasia në stok</th>
                          </tr>
                        </thead>
                        <tbody>
                          {produkt.stok.map((s, i) => (
                            <tr key={i}>
                              <td>{s.madhesi?.emri}</td>
                              <td>{s.ngjyra?.emri}</td>
                              <td className={`font-semibold ${s.sasia > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{s.sasia}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : query && !isLoading && (
        <div className="empty-state">
          <div className="empty-state-icon">
            <PackageSearch size={64} className="text-gray-300 dark:text-dark-600" />
          </div>
          <h3 className="empty-state-title">Asnjë produkt nuk u gjet</h3>
          <p className="empty-state-description">Provoni me një kërkim tjetër</p>
        </div>
      )}
    </div>
  );
}

export default KerkoStokShpejt;
